import { z } from 'zod';

import { getTranslation } from '../../../../utils/get-translation.util';

export const MAX_FILE_SIZE = 1024 * 1024 * 10;

export const ACCEPTED_FILE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg',
];

export const fileSchemaMessage = getTranslation('FILE_SIZE_TOO_LARGE');

const commonStock = z.object({
  stockItems: z
    .array(
      z
        .object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z.object({
                strikePrice: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                conversionRatio: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                pool: z.object({
                  shareClass: z.object({
                    name: z
                      .string()
                      .trim()
                      .min(1, { message: getTranslation('REQUIRED') }),
                  }),
                }),
              }),
            }),
          }),
          date: z.coerce.date(),
          exercised: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
        })
        .refine(
          ({ exercised, grant }) => {
            return exercised <= grant.vestedSharesCount - grant.exercisedCount;
          },
          {
            message: getTranslation('OPTIONS_MUST_BE_LESS_THAN_BALANCE'),
            path: ['exercised'],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') }),
});

const sar = z.object({
  sarItems: z
    .array(
      z
        .object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z.object({
                sarBasePrice: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                pool: z.object({
                  shareClass: z.object({
                    name: z
                      .string()
                      .trim()
                      .min(1, { message: getTranslation('REQUIRED') }),
                  }),
                }),
              }),
            }),
          }),
          sharePrice: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
          date: z.coerce.date(),
          sarCount: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
          sarPayout: z.coerce.number({
            message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
          }),
          issuedShares: z.coerce.number({
            message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
          }),
        })
        .refine(
          ({ sarCount, grant }) => {
            return sarCount <= grant.vestedSharesCount - grant.exercisedCount;
          },
          {
            message: getTranslation('SARS_MUST_BE_LESS_THAN_BALANCE'),
            path: ['sarCount'],
          },
        )
        .refine(
          ({ sarCount, grant, sarPayout, issuedShares, sharePrice }) => {
            return (
              sarCount ===
              (issuedShares * sharePrice) / (sharePrice - grant.grantItem.plan.sarBasePrice) +
                sarPayout / (sharePrice - grant.grantItem.plan.sarBasePrice)
            );
          },
          {
            message: getTranslation('ALL_SARS_MUST_BE_DISTRIBUTED'),
            path: [],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') })
    .max(10, { message: getTranslation('ITEMS_MAXIMUM', { count: 10 }) }),
});

const phantom = z.object({
  phantomItems: z
    .array(
      z
        .object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
          }),
          date: z.coerce.date(),
          sharePrice: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
          phantomShares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
        })
        .refine(
          ({ phantomShares, grant }) => {
            return phantomShares <= grant.vestedSharesCount - grant.exercisedCount;
          },
          {
            message: getTranslation('SHARES_MUST_BE_LESS_THAN_BALANCE'),
            path: ['phantomShares'],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') }),
});

const hurdle = z.object({
  hurdleItems: z
    .array(
      z
        .object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z.object({
                hardleValue: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                pool: z.object({
                  shareClass: z.object({
                    name: z
                      .string()
                      .trim()
                      .min(1, { message: getTranslation('REQUIRED') }),
                  }),
                }),
              }),
            }),
          }),
          date: z.coerce.date(),
          issuedShares: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
        })
        .refine(
          ({ issuedShares, grant }) => {
            return issuedShares <= grant.vestedSharesCount - grant.exercisedCount;
          },
          {
            message: getTranslation('SHARES_MUST_BE_LESS_THAN_BALANCE'),
            path: ['issuedShares'],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') }),
});

const bspce = z.object({
  bspceItems: z
    .array(
      z
        .object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z.object({
                strikePrice: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                conversionRatio: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                pool: z.object({
                  shareClass: z.object({
                    name: z
                      .string()
                      .trim()
                      .min(1, { message: getTranslation('REQUIRED') }),
                  }),
                }),
              }),
            }),
          }),
          date: z.coerce.date(),
          exercised: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
        })
        .refine(
          ({ exercised, grant }) => {
            return exercised <= grant.vestedSharesCount - grant.exercisedCount;
          },
          {
            message: getTranslation('OPTIONS_MUST_BE_LESS_THAN_BALANCE'),
            path: ['exercised'],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') }),
});

// const rsa = z.object({
//   rsaItems: z
//     .array(
//       z
//         .object({
//           stakeholder: z.object({
//             fullName: z
//               .string()
//               .trim()
//               .min(1, { message: getTranslation("REQUIRED") }),
//             id: z
//               .string()
//               .trim()
//               .min(1, { message: getTranslation("REQUIRED") }),
//           }),
//           grant: z.object({
//             id: z
//               .string()
//               .trim()
//               .min(1, { message: getTranslation("REQUIRED") }),
//             exercisedCount: z.coerce.number({ message: getTranslation("PLEASE_ENTER_NUMERIC_VALUE") }).min(0),
//             vestedSharesCount: z.coerce.number({ message: getTranslation("PLEASE_ENTER_NUMERIC_VALUE") }),
//             grantItem: z.object({
//               plan: z.object({
//                 purchasePrice: z.coerce.number({ message: getTranslation("PLEASE_ENTER_NUMERIC_VALUE") }),
//                 conversionRatio: z.coerce.number({ message: getTranslation("PLEASE_ENTER_NUMERIC_VALUE") }),
//                 pool: z.object({
//                   shareClass: z.object({
//                     name: z
//                       .string()
//                       .trim()
//                       .min(1, { message: getTranslation("REQUIRED") }),
//                   }),
//                 }),
//               }),
//             }),
//           }),
//           date: z.coerce.date(),
//           exercised: z.coerce.number({ message: getTranslation("PLEASE_ENTER_NUMERIC_VALUE") }).min(1, { message: getTranslation("REQUIRED") }),
//         })
//         .refine(
//           ({ exercised, grant }) => {
//             return (
//               exercised <=
//               grant.vestedSharesCount / (grant.grantItem.plan.conversionRatio || 1) -
//                 grant.exercisedCount / (grant.grantItem.plan.conversionRatio || 1)
//             );
//           },
//           {
//             message: getTranslation("OPTIONS_MUST_BE_LESS_THAN_BALANCE"),
//             path: ['exercised'],
//           },
//         ),
//     )
//     .min(1, { message: getTranslation("REQUIRED") }),
// });

const warrantExercise = z.object({
  warrantExerciseItems: z
    .array(
      z
        .object({
          stakeholder: z.object({
            fullName: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
          }),
          grant: z.object({
            id: z
              .string()
              .trim()
              .min(1, { message: getTranslation('REQUIRED') }),
            exercisedCount: z.coerce
              .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
              .min(0),
            vestedSharesCount: z.coerce.number({
              message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
            }),
            grantItem: z.object({
              plan: z.object({
                warrantPrice: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                conversionRatio: z.coerce.number({
                  message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
                }),
                pool: z.object({
                  shareClass: z.object({
                    name: z
                      .string()
                      .trim()
                      .min(1, { message: getTranslation('REQUIRED') }),
                  }),
                }),
              }),
            }),
          }),
          date: z.coerce.date(),
          exercised: z.coerce
            .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
            .min(1, { message: getTranslation('REQUIRED') }),
        })
        .refine(
          ({ exercised, grant }) => {
            return exercised <= grant.vestedSharesCount - grant.exercisedCount;
          },
          {
            message: getTranslation('OPTIONS_MUST_BE_LESS_THAN_BALANCE'),
            path: ['exercised'],
          },
        ),
    )
    .min(1, { message: getTranslation('REQUIRED') }),
});

export const stepOne = z.object({
  commonStock: commonStock.optional(),
  sar: sar.optional(),
  phantom: phantom.optional(),
  hurdle: hurdle.optional(),
  bspce: bspce.optional(),
  // rsa: rsa.optional(),
  warrantExercise: warrantExercise.optional(),
});

export const stepTwo = z
  .object({
    additionalNotes: z
      .string()
      .max(3000, getTranslation('MAXIMUM_CHARACTERS', { count: 3000 }))
      .optional(),
    files: z
      .object({
        docLink: z.string(),
        loadProgress: z.number().max(100),
        abort: z.function(),
        id: z.string(),
        doc: z
          .custom<File>()
          .or(
            z.object({
              size: z.number(),
              type: z.string(),
              name: z.string(),
            }),
          )
          .refine(
            ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
            getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
          )
          .refine(({ size }) => size <= MAX_FILE_SIZE, fileSchemaMessage),
      })
      .array()

      .optional(),
  })
  .optional();

export const formSchema = z.object({
  stepOne,
  stepTwo,
});

export type FormSchema = z.infer<typeof formSchema>;
